import React from "react";
import { Box, TableCell, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DotLegalMultiSelect, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import QuestionnaireStatus from "@common/components/questionnaireComponents/questionnaireStatus/QuestionnaireStatus";
import MentionInputRender from "@common/components/mentionInput/MentionInputRender";
import { useTranslation } from "@localization/useTranslation";
import { LegalEntityAuditStatus } from "../../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import InternalAssessmentReportButtons from "../../internalAssessmentReportButtons/InternalAssessmentReportButtons";
import { InternalAssessmentReportRow, InternalAssessmentReportTableProps } from "../InternalAssessmentReportTable.types";
import useInternalAssessmentReportInProgressAssessments from "./InternalAssessmentReportInProgressAssessments.hooks";
import ManagementReportTableSection, {
    managementReportFilterGridSizes,
} from "@common/components/managementReport/managementReportTableSection/ManagementReportTableSection";

function InternalAssessmentReportInProgressAssessments(props: InternalAssessmentReportTableProps) {
    const { translateString, translateLegalEntityAuditStatus } = useTranslation();
    const {
        rows,
        onDownloadReportAsPdf,
        getReportPdfUrl,
        isDownloadingPdf,
        searchedStatus,
        setSearchedStatus,
        statusFilterOptions,
        searchedRemindersSent,
        setSearchedRemindersSent,
        remindersSentFilterOptions,
    } = useInternalAssessmentReportInProgressAssessments(props);

    function getHeaders() {
        const headers = Array<ITableHeader<InternalAssessmentReportRow>>();
        headers.push({ property: "assigneeName", text: translateString("assigned"), align: "left", showOnMobile: true });
        headers.push({ property: "statusString", text: translateString("status"), align: "left", showOnMobile: true });
        headers.push({ property: "reminderSentString", text: translateString("headerReminderSent"), align: "left", showOnMobile: true });
        headers.push({ property: "questionnaireId", text: translateString("assessmentReport"), align: "left", showOnMobile: true });
        headers.push({ property: "note", text: translateString("note"), align: "left", showOnMobile: true });

        return headers;
    }

    function getSearchFields() {
        return (
            <React.Fragment>
                <Grid size={managementReportFilterGridSizes}>
                    <DotLegalMultiSelect
                        onChange={setSearchedStatus}
                        chosenOptions={searchedStatus}
                        label={translateString("status")}
                        placeholder={translateString("status")}
                        isLoading={props.isLoading}
                        options={statusFilterOptions}
                        noMargin
                        limit={1}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
                <Grid size={managementReportFilterGridSizes}>
                    <DotLegalMultiSelect
                        onChange={setSearchedRemindersSent}
                        chosenOptions={searchedRemindersSent}
                        label={translateString("headerReminderSent")}
                        placeholder={translateString("headerReminderSent")}
                        isLoading={props.isLoading}
                        options={remindersSentFilterOptions}
                        noMargin
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
            </React.Fragment>
        );
    }

    return (
        <ManagementReportTableSection
            title={translateLegalEntityAuditStatus(LegalEntityAuditStatus.InProgress.toString())}
            count={props.assessments.length}
            isPdf={props.isPdf}
            headers={getHeaders()}
            defaultOrderBy={"assigneeName"}
            data={rows}
            searchFields={getSearchFields()}
            renderRow={(row) => (
                <TableRow key={row.id}>
                    <TableCell>
                        <Box component={"span"} sx={{ fontWeight: "bold" }}>
                            {row.assigneeName}
                        </Box>
                    </TableCell>
                    <TableCell>
                        <QuestionnaireStatus
                            showQuestionsStatistics={row.showQuestionsStatistics}
                            numberOfQuestionsAnswered={row.numberOfQuestionsAnswered}
                            totalNumberOfQuestions={row.totalNumberOfQuestions}
                            status={row.status}
                            lastestRecipientUpdate={row.lastestRecipientUpdate}
                        />
                    </TableCell>
                    <TableCell>{row.remindersPaused ? translateString("remindersPaused") : row.reminderSentString}</TableCell>
                    <TableCell>
                        <InternalAssessmentReportButtons
                            row={row}
                            isDownloadingPdf={isDownloadingPdf(row.id)}
                            reportPdfUrl={getReportPdfUrl(row, false, row.hasRecommendations)}
                            onDownloadReportPdf={onDownloadReportAsPdf}
                        />
                    </TableCell>
                    <TableCell>
                        <MentionInputRender value={row.note ?? ""} />
                    </TableCell>
                </TableRow>
            )}
        />
    );
}

export default InternalAssessmentReportInProgressAssessments;
